import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react"
import { AnimatePresence, motion } from "framer-motion"
import {
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clapperboard,
  Copy,
  Download,
  LoaderCircle,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react"
import { toast } from "react-hot-toast"

import {
  createGestockAdVideo,
  deleteGestockAdVideo,
  getGestockAdVideo,
  getGestockAdVideos,
  uploadVehicleCarImagesToS3,
} from "../../api/products/post"

type Props = {
  isOpen: boolean
  onClose: () => void
  storeId?: string
  productId?: string
  generationMode?: "classic" | "cinematic_ai"
  productName: string
  productCategory?: string
  images: Array<{
    key?: string
    url: string
    previewUrl?: string
  }>
  defaultPhone?: string
}

type ModalImage = {
  id: string
  key: string
  url: string
  previewUrl: string
  file?: File
}

const PLATFORM_OPTIONS = [
  { value: "tiktok", label: "TikTok", hint: "Vertical, plus dynamique" },
  { value: "facebook", label: "Facebook", hint: "Reel ou story produit" },
  { value: "whatsapp", label: "WhatsApp", hint: "Status rapide à partager" },
]

const STYLE_OPTIONS = [
  { value: "clean", label: "Clean", hint: "Sobre, premium Gestock" },
  { value: "luxury", label: "Luxury", hint: "Plus élégant, plus posé" },
  { value: "flash", label: "Flash", hint: "Rythme promo plus agressif" },
  { value: "showroom", label: "Showroom", hint: "Look auto / catalogue moderne" },
]

const MOTION_STYLE_OPTIONS = [
  { value: "cinematic", label: "Cinematic" },
  { value: "smooth", label: "Smooth" },
  { value: "energetic", label: "Energetic" },
  { value: "showroom", label: "Showroom" },
]

const MUSIC_MOOD_OPTIONS = [
  { value: "uplifting", label: "Uplifting" },
  { value: "luxury", label: "Luxury" },
  { value: "corporate", label: "Corporate" },
  { value: "fast_promo", label: "Fast Promo" },
]

function buildPromoDraft({
  productName,
  productCategory,
  defaultPhone,
  platformPreset,
  stylePreset,
  generationMode,
}: {
  productName: string
  productCategory?: string
  defaultPhone?: string
  platformPreset: string
  stylePreset: string
  generationMode: "classic" | "cinematic_ai"
}) {
  const safeName = (productName || "Produit").trim()
  const category = String(productCategory || "").trim().toUpperCase()
  const isCar = category.includes("CAR") || category.includes("AUTO")
  const isProperty = category.includes("PROPERTY") || category.includes("REAL") || category.includes("IMMO")

  const styleLabel =
    stylePreset === "luxury"
      ? "premium"
      : stylePreset === "flash"
        ? "promo"
        : stylePreset === "showroom"
          ? "showroom"
          : "soigne"

  const headline = isCar
    ? `${safeName} disponible maintenant`
    : isProperty
      ? `${safeName} a découvrir sans tarder`
      : `${safeName} en promo cette semaine`

  const cta =
    platformPreset === "whatsapp"
      ? "Répondez maintenant pour plus d'informations"
      : platformPreset === "facebook"
        ? "Envoyez-nous un message dès maintenant"
        : "Contactez-nous maintenant"

  const contactLine = defaultPhone ? `Contact: ${defaultPhone}` : "Contactez notre boutique"

  const creativeBrief =
    generationMode === "cinematic_ai"
      ? isCar
        ? `Créer une pub verticale ${styleLabel} avec mouvements de caméra fluides, détails extérieurs et intérieurs, rendu réaliste et ambiance concession moderne autour de ${safeName}.`
        : isProperty
          ? `Créer une pub verticale ${styleLabel} avec reveal élégant des espaces, ambiance lumineuse, mouvements de caméra doux et rendu réaliste pour mettre ${safeName} en valeur.`
          : `Créer une pub verticale ${styleLabel} avec mise en avant produit claire, mouvements subtils, lumière propre et rendu social media réaliste pour ${safeName}.`
      : ""

  return { headline, cta, contactLine, creativeBrief }
}

function buildRunwayPromptPreview({
  productName,
  platformPreset,
  stylePreset,
  headline,
  cta,
  contactLine,
  creativeBrief,
  motionStyle,
  musicMood,
  voiceover,
}: {
  productName: string
  platformPreset: string
  stylePreset: string
  headline: string
  cta: string
  contactLine: string
  creativeBrief: string
  motionStyle: string
  musicMood: string
  voiceover: boolean
}) {
  const safeName = (productName || "Produit").trim()
  const styleText =
    stylePreset === "luxury"
      ? "cinematic luxury social media advertisement"
      : stylePreset === "flash"
        ? "high energy promotional vertical ad"
        : stylePreset === "showroom"
          ? "modern showroom style vertical ad"
          : "clean premium vertical social ad"

  const directionBits = [headline, cta, contactLine].filter(Boolean)

  return [
    `Create a ${styleText} for ${safeName}.`,
    "Keep the product realistic and faithful to the provided image.",
    `Use ${motionStyle} motion, premium lighting, subtle motion, and ad-like pacing.`,
    `Frame for a 9:16 ${platformPreset} mobile video suitable for social media.`,
    `The intended background music mood is ${musicMood}.`,
    voiceover ? "Leave visual pacing suitable for later voiceover sync." : "Make the visuals self-explanatory without needing narration.",
    creativeBrief ? `Creative brief: ${creativeBrief}` : "",
    directionBits.length ? `Ad direction: ${directionBits.join(" | ")}` : "",
  ]
    .filter(Boolean)
    .join(" ")
}

export default function GestockAdVideoModal({
  isOpen,
  onClose,
  storeId,
  productId,
  generationMode = "classic",
  productName,
  productCategory,
  images,
  defaultPhone,
}: Props) {
  const isCinematicAi = generationMode === "cinematic_ai"
  const baseImages = useMemo<ModalImage[]>(
    () =>
      (images || [])
        .map((image, index) => ({
          id: String(image?.key || image?.previewUrl || image?.url || index),
          key: String(image?.key || "").trim(),
          url: String(image?.url || "").trim(),
          previewUrl: String(image?.previewUrl || image?.url || "").trim(),
        }))
        .filter((image) => image.previewUrl || image.url),
    [images]
  )
  const [extraImages, setExtraImages] = useState<
    Array<{
      id: string
      file: File
      previewUrl: string
    }>
  >([])
  const usableImages = useMemo<ModalImage[]>(
    () => [
      ...baseImages,
      ...extraImages.map((image) => ({
        id: image.id,
        key: "",
        url: "",
        previewUrl: image.previewUrl,
        file: image.file,
      })),
    ],
    [baseImages, extraImages]
  )
  const defaultSelectedImages = useMemo(() => usableImages.slice(0, Math.min(3, usableImages.length)), [usableImages])

  const [platformPreset, setPlatformPreset] = useState("tiktok")
  const [stylePreset, setStylePreset] = useState("clean")
  const [selectedImageIds, setSelectedImageIds] = useState<string[]>(defaultSelectedImages.map((image) => image.id))
  const [headline, setHeadline] = useState("")
  const [cta, setCta] = useState("")
  const [contactLine, setContactLine] = useState(defaultPhone ? `Contact: ${defaultPhone}` : "")
  const [creativeBrief, setCreativeBrief] = useState("")
  const [motionStyle, setMotionStyle] = useState("cinematic")
  const [voiceover, setVoiceover] = useState(false)
  const [musicMood, setMusicMood] = useState("uplifting")
  const [submitting, setSubmitting] = useState(false)
  const [uploadingImages, setUploadingImages] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [history, setHistory] = useState<any[]>([])
  const [activeVideo, setActiveVideo] = useState<any | null>(null)
  const [deletingId, setDeletingId] = useState("")
  const [showAdvancedPrompt, setShowAdvancedPrompt] = useState(false)
  const promoDraft = useMemo(
    () =>
      buildPromoDraft({
        productName,
        productCategory,
        defaultPhone,
        platformPreset,
        stylePreset,
        generationMode,
      }),
    [defaultPhone, generationMode, platformPreset, productCategory, productName, stylePreset]
  )
  const runwayPromptPreview = useMemo(
    () =>
      buildRunwayPromptPreview({
        productName,
        platformPreset,
        stylePreset,
        headline: headline.trim(),
        cta: cta.trim(),
        contactLine: contactLine.trim(),
        creativeBrief: creativeBrief.trim(),
        motionStyle,
        musicMood,
        voiceover,
      }),
    [contactLine, creativeBrief, cta, headline, motionStyle, musicMood, platformPreset, productName, stylePreset, voiceover]
  )

  const refreshHistory = useCallback(async () => {
    if (!storeId || !productId) return

    try {
      setLoadingHistory(true)
      const rows = await getGestockAdVideos(storeId, { productId })
      setHistory(Array.isArray(rows) ? rows : [])
    } catch (error: any) {
      toast.error(error?.message || "Impossible de charger les vidéos publicitaires")
    } finally {
      setLoadingHistory(false)
    }
  }, [productId, storeId])

  useEffect(() => {
    if (!isOpen) return
    setSelectedImageIds(defaultSelectedImages.map((image) => image.id))
    setHeadline(promoDraft.headline)
    setCta(promoDraft.cta)
    setContactLine(promoDraft.contactLine)
    setCreativeBrief(promoDraft.creativeBrief)
    setMotionStyle("cinematic")
    setVoiceover(false)
    setMusicMood("uplifting")
    setShowAdvancedPrompt(false)
    setActiveVideo(null)
    refreshHistory()
  }, [defaultSelectedImages, isOpen, productId, promoDraft, refreshHistory, storeId])

  useEffect(() => {
    return () => {
      extraImages.forEach((image) => URL.revokeObjectURL(image.previewUrl))
    }
  }, [extraImages])

  useEffect(() => {
    if (!isOpen || !storeId || !activeVideo?._id) return
    if (!["queued", "processing"].includes(String(activeVideo.status || ""))) return

    const interval = window.setInterval(async () => {
      try {
        const latest = await getGestockAdVideo(storeId, activeVideo._id)
        setActiveVideo(latest)
        if (["completed", "failed"].includes(String(latest?.status || ""))) {
          refreshHistory()
        }
      } catch {
        // keep polling silently for transient errors
      }
    }, 2500)

    return () => window.clearInterval(interval)
  }, [activeVideo, isOpen, refreshHistory, storeId])

  const toggleImage = (imageId: string) => {
    setSelectedImageIds((current) => {
      if (current.includes(imageId)) {
        return current.filter((value) => value !== imageId)
      }

      if (current.length >= 5) {
        toast.error("Vous pouvez sélectionner au maximum 5 images.")
        return current
      }

      return [...current, imageId]
    })
  }

  const handleAddImages = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (!files.length) return

    const invalidFile = files.find((file) => !file.type.startsWith("image/"))
    if (invalidFile) {
      toast.error("Tous les fichiers ajoutés doivent être des images.")
      event.target.value = ""
      return
    }

    const oversizedFile = files.find((file) => file.size > 15 * 1024 * 1024)
    if (oversizedFile) {
      toast.error("Chaque image ajoutée doit faire moins de 15 MB.")
      event.target.value = ""
      return
    }

    const availableSlots = Math.max(0, 5 - usableImages.length)
    if (availableSlots <= 0) {
      toast.error("Vous pouvez utiliser au maximum 5 images pour une pub vidéo.")
      event.target.value = ""
      return
    }

    const nextFiles = files.slice(0, availableSlots)
    const nextExtraImages = nextFiles.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }))

    setExtraImages((current) => [...current, ...nextExtraImages])
    setSelectedImageIds((current) => [...current, ...nextExtraImages.map((image) => image.id)])
    event.target.value = ""
  }

  const handleRemoveExtraImage = (imageId: string) => {
    setExtraImages((current) => {
      const target = current.find((image) => image.id === imageId)
      if (target) URL.revokeObjectURL(target.previewUrl)
      return current.filter((image) => image.id !== imageId)
    })
    setSelectedImageIds((current) => current.filter((id) => id !== imageId))
  }

  const handleGeneratePromoText = () => {
    setHeadline(promoDraft.headline)
    setCta(promoDraft.cta)
    setContactLine(promoDraft.contactLine)
    if (isCinematicAi) {
      setCreativeBrief(promoDraft.creativeBrief)
    }
    toast.success("Texte promo généré")
  }

  const handleGenerate = async () => {
    if (!storeId || !productId) return
    const minimumImages = isCinematicAi ? 1 : 2
    if (selectedImageIds.length < minimumImages) {
      toast.error(
        isCinematicAi
          ? "Sélectionnez au moins 1 image pour générer une pub vidéo Pro."
          : "Sélectionnez au moins 2 images pour générer une pub vidéo."
      )
      return
    }

    const selectedImages = usableImages.filter((image) => selectedImageIds.includes(image.id))

    try {
      setSubmitting(true)
      const pendingFiles = selectedImages
        .map((image) => image.file)
        .filter(Boolean) as File[]

      let uploadedExtraImages: Array<{ imageUrl: string; coverImageUrl: string; coverImageKey: string }> = []
      if (pendingFiles.length) {
        setUploadingImages(true)
        uploadedExtraImages = await uploadVehicleCarImagesToS3(pendingFiles, storeId)
      }

      let uploadedIndex = 0
      const resolvedSelectedImages = selectedImages.map((image) => {
        if (!image.file) {
          return {
            url: image.previewUrl || image.url,
            key: image.key,
          }
        }

        const uploaded = uploadedExtraImages[uploadedIndex]
        uploadedIndex += 1
        return {
          url: uploaded?.imageUrl || uploaded?.coverImageUrl || "",
          key: uploaded?.coverImageKey || "",
        }
      })

      const created = await createGestockAdVideo(storeId, productId, {
        platformPreset,
        stylePreset,
        generationMode,
        selectedImageUrls: resolvedSelectedImages.map((image) => image.url).filter(Boolean),
        selectedImageKeys: resolvedSelectedImages.map((image) => image.key).filter(Boolean),
        headline: headline.trim(),
        cta: cta.trim(),
        contactLine: contactLine.trim(),
        creativeBrief: creativeBrief.trim(),
        motionStyle,
        voiceover,
        musicMood,
      })
      setActiveVideo(created)
      await refreshHistory()
      toast.success(isCinematicAi ? "AI Ad Video Pro lancée" : "GESTOCK AI Ad Video lancée")
    } catch (error: any) {
      toast.error(error?.message || "Impossible de lancer la génération")
    } finally {
      setUploadingImages(false)
      setSubmitting(false)
    }
  }

  const handleCopyCaption = async (caption: string) => {
    try {
      await navigator.clipboard.writeText(caption)
      toast.success("Caption copiée")
    } catch {
      toast.error("Impossible de copier le caption")
    }
  }

  const handleDeleteVideo = async (videoId: string) => {
    if (!storeId || !videoId) return

    try {
      setDeletingId(videoId)
      await deleteGestockAdVideo(storeId, videoId)
      if (activeVideo?._id === videoId) setActiveVideo(null)
      await refreshHistory()
      toast.success("Vidéo publicitaire supprimée")
    } catch (error: any) {
      toast.error(error?.message || "Suppression impossible")
    } finally {
      setDeletingId("")
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/55 p-4"
      >
        <motion.div
          initial={{ y: 18, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 12, opacity: 0, scale: 0.98 }}
          className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl"
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 bg-[linear-gradient(135deg,_#082f49,_#0f766e,_#164e63)] px-6 py-5 text-white">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] uppercase tracking-[0.18em]">
                <Sparkles size={12} />
                {isCinematicAi ? "AI Ad Video Pro" : "Gestock Ai Ad Video"}
              </p>
              <h2 className="mt-3 text-xl font-semibold">{productName || "Produit"}</h2>
              <p className="mt-1 text-sm text-white/80">
                {isCinematicAi
                  ? "Préparez une génération cinematic AI à partir d'une ou plusieurs images produit via le moteur vidéo externe."
                  : "Sélectionnez 2 à 5 images pour générer une mini pub verticale prête pour TikTok, Facebook ou WhatsApp."}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-white/10 p-2 text-white transition hover:bg-white/20"
            >
              <X size={18} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="grid gap-0 lg:grid-cols-[minmax(0,1.15fr),420px]">
            <div className="space-y-6 p-6">
              <section className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Images sélectionnées</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {selectedImageIds.length} sélectionnée(s) sur {usableImages.length}
                    </p>
                  </div>
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
                    {productCategory || "Produit"}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100">
                    <Plus size={16} />
                    Ajouter des images
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handleAddImages} />
                  </label>
                  <p className="text-xs text-slate-500">
                    {isCinematicAi
                      ? "Le mode Pro peut démarrer avec 1 image, mais vous pouvez en ajouter plusieurs ici."
                      : "Même si le produit n'a aucune image, vous pouvez en ajouter ici pour générer la pub."}
                  </p>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {usableImages.map((image) => {
                    const selected = selectedImageIds.includes(image.id)
                    return (
                      <div
                        key={image.id}
                        className={`overflow-hidden rounded-2xl border transition ${
                          selected ? "border-cyan-500 ring-2 ring-cyan-200" : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleImage(image.id)}
                          className="block w-full"
                        >
                          <img src={image.previewUrl || image.url} alt="product" className="h-32 w-full object-cover" />
                        </button>
                        {image.file && (
                          <div className="flex items-center justify-between gap-2 border-t border-slate-100 bg-white px-3 py-2">
                            <span className="truncate text-[11px] text-slate-500">Ajout local</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveExtraImage(image.id)}
                              className="text-xs font-medium text-rose-600 hover:text-rose-700"
                            >
                              Retirer
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </section>

              <section className="grid gap-4 md:grid-cols-2">
                <div className="rounded-3xl border border-slate-200 p-5">
                  <p className="text-sm font-semibold text-slate-900">Preset plateforme</p>
                  <div className="mt-4 space-y-3">
                    {PLATFORM_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setPlatformPreset(option.value)}
                        className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                          platformPreset === option.value
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-white"
                        }`}
                      >
                        <p className="font-medium">{option.label}</p>
                        <p className={`mt-1 text-xs ${platformPreset === option.value ? "text-white/75" : "text-slate-500"}`}>
                          {option.hint}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 p-5">
                  <p className="text-sm font-semibold text-slate-900">Style visuel</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {STYLE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setStylePreset(option.value)}
                        className={`rounded-2xl border px-4 py-3 text-left transition ${
                          stylePreset === option.value
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-white"
                        }`}
                      >
                        <p className="font-medium">{option.label}</p>
                        <p className={`mt-1 text-xs ${stylePreset === option.value ? "text-white/75" : "text-slate-500"}`}>
                          {option.hint}
                        </p>
                      </button>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                    Le rendu Phase 2 ajoute un habillage Gestock avec branding logo intégré dans la vidéo.
                  </p>
                </div>
              </section>

              <section className="grid gap-4 md:grid-cols-1">
                <div className="rounded-3xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-900">Texte promo</p>
                    <button
                      type="button"
                      onClick={handleGeneratePromoText}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-white"
                    >
                      <Sparkles size={13} />
                      Générer le texte promo
                    </button>
                  </div>
                  <div className="mt-4 space-y-3">
                    <Field label="Headline personnalisé">
                      <input
                        value={headline}
                        onChange={(e) => setHeadline(e.target.value)}
                        placeholder="Ex: Toyota RAV4 2022 en très bon état"
                        className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-slate-900/10"
                      />
                    </Field>
                    <Field label="CTA">
                      <input
                        value={cta}
                        onChange={(e) => setCta(e.target.value)}
                        placeholder="Ex: Contactez-nous maintenant"
                        className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-slate-900/10"
                      />
                    </Field>
                    <Field label="Ligne de contact">
                      <input
                        value={contactLine}
                        onChange={(e) => setContactLine(e.target.value)}
                        placeholder="Ex: Contact: +225 07 00 00 00 00"
                        className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-slate-900/10"
                      />
                    </Field>
                  </div>
                </div>
              </section>

              {isCinematicAi && (
                <section className="grid gap-4 md:grid-cols-1">
                  <div className="rounded-3xl border border-amber-200 bg-amber-50/60 p-5">
                    <p className="text-sm font-semibold text-slate-900">Direction cinematic AI</p>
                    <div className="mt-4 space-y-3">
                      <Field label="Creative brief">
                        <div className="mt-1 rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm leading-6 text-slate-700">
                          {creativeBrief.trim() || "Aucun brief creatif disponible."}
                        </div>
                      </Field>
                      <div className="grid gap-4 md:grid-cols-2">
                        <Field label="Motion style">
                          <select
                            value={motionStyle}
                            onChange={(e) => setMotionStyle(e.target.value)}
                            className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-slate-900/10"
                          >
                            {MOTION_STYLE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Music mood">
                          <select
                            value={musicMood}
                            onChange={(e) => setMusicMood(e.target.value)}
                            className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-slate-900/10"
                          >
                            {MUSIC_MOOD_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>
                      <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={voiceover}
                          onChange={(e) => setVoiceover(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                        Préparer la vidéo pour une future voiceover
                      </label>
                    </div>
                  </div>
                </section>
              )}

              {isCinematicAi && (
                <section className="grid gap-4 md:grid-cols-1">
                  <div className="rounded-3xl border border-slate-200 bg-slate-950 p-5 text-white">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold">Resume AI</p>
                        <p className="mt-1 text-xs text-white/70">
                          Apercu simple de la direction envoyee au moteur cinematic AI.
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                        <p className="text-[11px] uppercase tracking-[0.16em] text-white/55">Style</p>
                        <p className="mt-1 text-sm text-white/90">{stylePreset}</p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                        <p className="text-[11px] uppercase tracking-[0.16em] text-white/55">Motion</p>
                        <p className="mt-1 text-sm text-white/90">{motionStyle}</p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                        <p className="text-[11px] uppercase tracking-[0.16em] text-white/55">Music mood</p>
                        <p className="mt-1 text-sm text-white/90">{musicMood}</p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                        <p className="text-[11px] uppercase tracking-[0.16em] text-white/55">Voiceover</p>
                        <p className="mt-1 text-sm text-white/90">{voiceover ? "Ready" : "Not requested"}</p>
                      </div>
                    </div>
                    <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 p-3">
                      <p className="text-[11px] uppercase tracking-[0.16em] text-white/55">Creative brief</p>
                      <p className="mt-1 text-sm leading-6 text-white/90">
                        {creativeBrief.trim() || "Aucun brief personnalise saisi."}
                      </p>
                    </div>
                    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5">
                      <button
                        type="button"
                        onClick={() => setShowAdvancedPrompt((value) => !value)}
                        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-white"
                      >
                        <span>Voir le prompt AI avance</span>
                        {showAdvancedPrompt ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      {showAdvancedPrompt && (
                        <div className="border-t border-white/10 px-4 py-4">
                          <div className="mb-3 flex items-center justify-end">
                            <button
                              type="button"
                              onClick={() => handleCopyCaption(runwayPromptPreview)}
                              className="inline-flex items-center gap-1 rounded-xl border border-white/15 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/10"
                            >
                              <Copy size={13} />
                              Copier
                            </button>
                          </div>
                          <pre className="whitespace-pre-wrap font-sans text-sm leading-6 text-white/90">
                            {runwayPromptPreview}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-500">
                  {isCinematicAi
                    ? "Mode cinematic AI: la génération Pro utilise maintenant un brief créatif et une direction visuelle dédiés pour Runway."
                    : "Version MVP: le backend génère une vidéo verticale simple à partir des images et prépare aussi le caption."}
                </p>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={submitting || !storeId || !productId}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
                >
                  {submitting ? <LoaderCircle size={16} className="animate-spin" /> : <Clapperboard size={16} />}
                  {uploadingImages
                    ? "Upload images..."
                    : submitting
                      ? "Lancement..."
                      : isCinematicAi
                        ? "Créer la pub vidéo Pro"
                        : "Créer la pub vidéo"}
                </button>
              </div>
            </div>

            <aside className="border-l border-slate-100 bg-slate-50/70 p-6">
              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Dernière génération</p>
                    <p className="mt-1 text-xs text-slate-500">Statut, caption et téléchargement</p>
                  </div>
                  {activeVideo?.status === "completed" && <CheckCircle2 size={18} className="text-emerald-600" />}
                </div>

                {!activeVideo && <p className="mt-4 text-sm text-slate-500">Aucune génération en cours.</p>}

                {activeVideo && (
                  <div className="mt-4 space-y-4">
                    <StatusBadge status={activeVideo.status} />

                    {activeVideo.videoPreviewUrl || activeVideo.videoUrl ? (
                      <video
                        src={activeVideo.videoPreviewUrl || activeVideo.videoUrl}
                        controls
                        className="w-full rounded-2xl border border-slate-200 bg-black"
                      />
                    ) : (
                      <div className="flex h-56 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
                        {activeVideo.status === "failed"
                          ? activeVideo.errorMessage || "La génération a échoué."
                          : "Le rendu vidéo est en cours..."}
                      </div>
                    )}

                    {activeVideo.script && (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Script</p>
                        <p className="mt-2 text-sm font-semibold text-slate-900">{activeVideo.script.headline}</p>
                        <p className="mt-2 text-sm text-slate-600">{activeVideo.script.hook}</p>
                      </div>
                    )}

                    {activeVideo.caption && (
                      <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-slate-900">Caption</p>
                          <button
                            type="button"
                            onClick={() => handleCopyCaption(activeVideo.caption)}
                            className="inline-flex items-center gap-1 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            <Copy size={13} />
                            Copier
                          </button>
                        </div>
                        <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-slate-600">{activeVideo.caption}</pre>
                      </div>
                    )}

                    {(activeVideo.videoPreviewUrl || activeVideo.videoUrl) && (
                      <a
                        href={activeVideo.videoPreviewUrl || activeVideo.videoUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-500"
                      >
                        <Download size={16} />
                        Télécharger la vidéo
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Historique</p>
                    <p className="mt-1 text-xs text-slate-500">Vos dernières pubs générées</p>
                  </div>
                  {loadingHistory && <LoaderCircle size={16} className="animate-spin text-slate-500" />}
                </div>

                <div className="mt-4 space-y-3">
                  {history.map((video) => (
                    <div key={video._id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{video?.script?.headline || productName}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {PLATFORM_OPTIONS.find((option) => option.value === video.platformPreset)?.label || video.platformPreset}
                          </p>
                        </div>
                        <StatusBadge status={video.status} compact />
                      </div>

                      <div className="mt-3 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setActiveVideo(video)}
                          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100"
                        >
                          Voir
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteVideo(video._id)}
                          disabled={deletingId === video._id}
                          className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-60"
                        >
                          <Trash2 size={13} />
                          {deletingId === video._id ? "Suppression..." : "Supprimer"}
                        </button>
                      </div>
                    </div>
                  ))}

                  {!loadingHistory && history.length === 0 && (
                    <p className="text-sm text-slate-500">Aucune pub générée pour ce produit pour le moment.</p>
                  )}
                </div>
              </div>
            </aside>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <label className="block text-sm text-slate-600">
      {label}
      {children}
    </label>
  )
}

function StatusBadge({
  status,
  compact = false,
}: {
  status: string
  compact?: boolean
}) {
  const normalized = String(status || "").toLowerCase()
  const config =
    normalized === "completed"
      ? { label: "Terminée", className: "border-emerald-200 bg-emerald-50 text-emerald-700" }
      : normalized === "failed"
        ? { label: "Échec", className: "border-rose-200 bg-rose-50 text-rose-700" }
        : normalized === "processing"
          ? { label: "En cours", className: "border-amber-200 bg-amber-50 text-amber-700" }
          : { label: "En attente", className: "border-sky-200 bg-sky-50 text-sky-700" }

  return (
    <span
      className={`inline-flex items-center rounded-full border py-1 text-xs font-semibold ${compact ? "px-2.5" : "px-3"} ${config.className}`}
    >
      {config.label}
    </span>
  )
}
